// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Jotai from "jotai";
import * as React from "react";
import * as Core__JSON from "@rescript/core/src/Core__JSON.bs.js";
import * as Core__Array from "@rescript/core/src/Core__Array.bs.js";
import * as Core__Option from "@rescript/core/src/Core__Option.bs.js";
import * as Core__Promise from "@rescript/core/src/Core__Promise.bs.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";
import * as RescriptReactRouter from "@rescript/react/src/RescriptReactRouter.bs.js";
import * as Utils$OrcaPaymentPage from "../Utilities/Utils.bs.js";
import * as Window$OrcaPaymentPage from "../Window.bs.js";
import * as CardUtils$OrcaPaymentPage from "../CardUtils.bs.js";
import * as JotaiAtoms$OrcaPaymentPage from "../Utilities/JotaiAtoms.bs.js";
import * as PaymentBody$OrcaPaymentPage from "../Utilities/PaymentBody.bs.js";
import * as VaultHelpers$OrcaPaymentPage from "./VaultHelpers.bs.js";
import * as CardFormShared$OrcaPaymentPage from "../Utilities/CardFormShared.bs.js";
import * as PaymentHelpers$OrcaPaymentPage from "../Utilities/PaymentHelpers.bs.js";
import * as SadPortRegistry$OrcaPaymentPage from "./SadPortRegistry.bs.js";
import * as PaymentEventData$OrcaPaymentPage from "../../shared-code/sdk-utils/events/PaymentEventData.bs.js";
import * as PaymentHelpersV2$OrcaPaymentPage from "../Utilities/PaymentHelpersV2.bs.js";
import * as CardFormPortProtocol$OrcaPaymentPage from "./CardFormPortProtocol.bs.js";
import * as PaymentSurfaceFamily$OrcaPaymentPage from "../Utilities/PaymentSurfaceFamily.bs.js";
import * as PaymentManagementBody$OrcaPaymentPage from "../Utilities/PaymentManagementBody.bs.js";
import * as SubscriptionEventHooks$OrcaPaymentPage from "../Hooks/SubscriptionEventHooks.bs.js";

function errorTypeToString(t) {
  switch (t) {
    case "ValidationError" :
      return "validation_error";
    case "ApiError" :
      return "api_error";
    case "CardError" :
      return "card_error";
  }
}

function defaultErrorMessage(code) {
  switch (code) {
    case "confirm_in_progress" :
      return "A confirm is already in flight for this payment method session";
    case "incomplete_field_set" :
      return "Mounted card fields are incomplete";
    case "session_consumed" :
      return "Payment method session has already been consumed or deinitialized";
    case "session_expired" :
      return "Payment method session has expired";
    case "tokenization_failed" :
      return "Card tokenization failed";
    case "tokenization_in_progress" :
      return "A tokenization is already in flight for this payment method session";
    case "validation_error" :
      return "Validation failed for one or more fields";
    default:
      return "An unexpected error occurred";
  }
}

function resolveErrorMessage(code, locale, fallback) {
  if (fallback !== undefined && fallback.length > 0) {
    return fallback;
  } else {
    return defaultErrorMessage(code);
  }
}

function makeErrorResult(code, message, localeOpt, typeOverride, param) {
  let locale = localeOpt !== undefined ? localeOpt : "en";
  let resolvedMessage = resolveErrorMessage(code, locale, message);
  let resolvedType;
  if (typeOverride !== undefined) {
    resolvedType = typeOverride;
  } else {
    switch (code) {
      case "confirm_in_progress" :
      case "session_consumed" :
      case "session_expired" :
      case "tokenization_failed" :
      case "tokenization_in_progress" :
        resolvedType = "ApiError";
        break;
      case "incomplete_field_set" :
      case "validation_error" :
        resolvedType = "ValidationError";
        break;
      default:
        resolvedType = "CardError";
    }
  }
  let errorDict = {};
  errorDict["code"] = code;
  errorDict["message"] = resolvedMessage;
  errorDict["type"] = errorTypeToString(resolvedType);
  let resultDict = {};
  resultDict["error"] = errorDict;
  return resultDict;
}

function buildConfirmResult(outcome) {
  if (outcome.TAG === "Success") {
    return outcome._0;
  }
  let payload = outcome._0;
  return makeErrorResult(payload.code, payload.message, payload.locale, payload.typeOverride, undefined);
}

function isErrorResult(result) {
  let dict = Utils$OrcaPaymentPage.getDictFromJson(result);
  return Core__Option.isSome(Core__Option.flatMap(dict["error"], Core__JSON.Decode.object));
}

function portKey(groupId, fieldName) {
  return groupId + `:` + fieldName;
}

function CardFormCoordinator(props) {
  let url = RescriptReactRouter.useUrl(undefined, undefined);
  let loggerState = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.loggerAtom);
  let keys = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.keys);
  let sessions = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.sessions);
  let paymentMethodListValue = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.paymentMethodList);
  let customPodUri = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.customPodUri);
  let componentName = CardUtils$OrcaPaymentPage.getQueryParamsDictforKey(url.search, "componentName");
  let surfaceFamilyStr = CardUtils$OrcaPaymentPage.getQueryParamsDictforKey(url.search, "surfaceFamily");
  let surfaceFamily = surfaceFamilyStr === "" ? undefined : surfaceFamilyStr;
  let groupId = CardUtils$OrcaPaymentPage.getQueryParamsDictforKey(url.search, "groupId");
  let coordinatorFamily = PaymentSurfaceFamily$OrcaPaymentPage.classifyCoordinatorFromUrl(componentName, surfaceFamily);
  let emitter = SubscriptionEventHooks$OrcaPaymentPage.useSubscriptionEventEmitter();
  let optionsPayment = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.optionAtom);
  let emitterRef = React.useRef(emitter);
  emitterRef.current = emitter;
  let subscribedEventsRef = React.useRef([]);
  subscribedEventsRef.current = Core__Option.getOr(optionsPayment.subscriptionEvents, []);
  let lastEmittedCardInfoRef = React.useRef("");
  let fieldSnapshotsRef = React.useRef({});
  let prevFocusReadyRef = React.useRef({});
  let lastBrandRef = React.useRef("");
  let confirmingRef = React.useRef(false);
  let match = React.useState(() => 0);
  let setRegistryVersion = match[1];
  let registryVersion = match[0];
  React.useEffect(() => {
    let onRegistryChange = () => setRegistryVersion(v => v + 1 | 0);
    SadPortRegistry$OrcaPaymentPage.addChangeListener(onRegistryChange);
    return () => SadPortRegistry$OrcaPaymentPage.removeChangeListener(onRegistryChange);
  }, []);
  let intent = PaymentHelpers$OrcaPaymentPage.usePaymentIntent(loggerState, "Card");
  let postConfirmResult = (confirmId, result) => Utils$OrcaPaymentPage.messageParentWindow(keys.parentURL, [
    [
      "confirmResult",
      result
    ],
    [
      "confirmId",
      confirmId
    ],
    [
      "iframeId",
      keys.iframeId
    ]
  ]);
  let vaultCredentials = React.useMemo(() => VaultHelpers$OrcaPaymentPage.getVaultCredentialsFromSessions(sessions), [sessions]);
  let aggregateRawValue = pick => Core__Array.reduce(Object.values(fieldSnapshotsRef.current), "", (acc, entry) => {
    if (acc !== "") {
      return acc;
    }
    let candidate = pick(Utils$OrcaPaymentPage.getDictFromJson(entry.payload));
    if (candidate !== "") {
      return candidate;
    } else {
      return acc;
    }
  });
  let aggregatedCardNumber = () => aggregateRawValue(d => Utils$OrcaPaymentPage.getString(d, "rawCardNumber", ""));
  let aggregatedCardExpiry = () => aggregateRawValue(d => Utils$OrcaPaymentPage.getString(d, "rawCardExpiry", ""));
  let aggregatedCvcNumber = () => aggregateRawValue(d => Utils$OrcaPaymentPage.getString(d, "rawCvc", ""));
  let aggregatedCardBrand = () => aggregateRawValue(d => Utils$OrcaPaymentPage.getString(d, "cardBrand", ""));
  let snapshotFieldStatus = fieldName => Core__Option.map(fieldSnapshotsRef.current[fieldName], entry => Utils$OrcaPaymentPage.getDictFromDict(Utils$OrcaPaymentPage.getDictFromJson(entry.payload), "fieldStatus"));
  let fieldConfirmedInvalid = (fieldName, hasStatusKey, isValidKey) => {
    let status = snapshotFieldStatus(fieldName);
    if (status === undefined) {
      return false;
    }
    let boolOf = (dict, key) => Core__Option.getOr(Core__Option.flatMap(dict[key], Core__JSON.Decode.bool), false);
    if (boolOf(status, hasStatusKey)) {
      return !boolOf(status, isValidKey);
    } else {
      return false;
    }
  };
  let fieldProblem = (fieldName, value, confirmedInvalid) => {
    if (value === "") {
      return fieldName + ` is required`;
    } else if (confirmedInvalid) {
      return fieldName + ` is invalid`;
    } else {
      return;
    }
  };
  let cvcFieldProblem = () => fieldProblem("cardCvc", aggregatedCvcNumber(), fieldConfirmedInvalid("cardCvc", "hasCvcValidationStatus", "isCvcValid"));
  let describeCardFieldProblems = () => Core__Array.filterMap([
    fieldProblem("cardNumber", aggregatedCardNumber(), fieldConfirmedInvalid("cardNumber", "hasCardValidationStatus", "isCardValid")),
    fieldProblem("cardExpiry", aggregatedCardExpiry(), fieldConfirmedInvalid("cardExpiry", "hasExpiryValidationStatus", "isExpiryValid")),
    cvcFieldProblem()
  ], problem => problem).join(", ");
  let relayDetectedBrandToCvc = brand => {
    SadPortRegistry$OrcaPaymentPage.postFrame(portKey(groupId, "cardCvc"), CardFormPortProtocol$OrcaPaymentPage.makePortFrame(CardFormPortProtocol$OrcaPaymentPage.kindDetectedCardBrand, brand));
  };
  let clearDependentFields = () => {
    [
      "cardExpiry",
      "cardCvc"
    ].forEach(fieldName => {
      SadPortRegistry$OrcaPaymentPage.postFrame(portKey(groupId, fieldName), CardFormPortProtocol$OrcaPaymentPage.makePortFrame(CardFormPortProtocol$OrcaPaymentPage.kindClearField, true));
    });
  };
  React.useEffect(() => {
    Object.keys(SadPortRegistry$OrcaPaymentPage.registry).filter(key => key.startsWith(groupId + `:`)).forEach(key => {
      let port = SadPortRegistry$OrcaPaymentPage.getPort(key);
      if (port === undefined) {
        return;
      }
      let fieldName = key.replace(groupId + `:`, "");
      Primitive_option.valFromOption(port).onmessage = ev => {
        let frameJson = ev.data;
        let match = CardFormPortProtocol$OrcaPaymentPage.decodePortFrame(frameJson);
        if (match !== undefined) {
          let kind = match.kind;
          if (kind === CardFormPortProtocol$OrcaPaymentPage.kindFieldStateUpdate) {
            let payload = match.payload;
            fieldSnapshotsRef.current[fieldName] = {
              payload: payload
            };
            if (PaymentEventData$OrcaPaymentPage.shouldEmitEvent("CardDetailsChange", subscribedEventsRef.current)) {
              let cardInfo = PaymentEventData$OrcaPaymentPage.buildCardInfo(aggregatedCardNumber(), aggregatedCardExpiry(), aggregatedCvcNumber(), aggregatedCardBrand());
              let serializedCardInfo = JSON.stringify(PaymentEventData$OrcaPaymentPage.cardInfoToJson(cardInfo));
              if (serializedCardInfo !== lastEmittedCardInfoRef.current) {
                lastEmittedCardInfoRef.current = serializedCardInfo;
                emitterRef.current.emitCardInfo("cardForm", cardInfo);
              }
            }
            if (fieldName === "cardNumber") {
              let payloadDict = Utils$OrcaPaymentPage.getDictFromJson(payload);
              let cardBrand = Utils$OrcaPaymentPage.getString(payloadDict, "cardBrand", "");
              let isCardNumberEmpty = Utils$OrcaPaymentPage.getBool(Utils$OrcaPaymentPage.getDictFromDict(payloadDict, "fieldStatus"), "empty", false);
              if (isCardNumberEmpty && lastBrandRef.current !== "") {
                lastBrandRef.current = "";
                clearDependentFields();
              } else if (cardBrand !== "" && cardBrand !== lastBrandRef.current) {
                lastBrandRef.current = cardBrand;
                relayDetectedBrandToCvc(cardBrand);
              }
            }
            let focusReady = Utils$OrcaPaymentPage.getBool(Utils$OrcaPaymentPage.getDictFromJson(payload), "focusReady", false);
            let prevReady = Core__Option.getOr(prevFocusReadyRef.current[fieldName], false);
            prevFocusReadyRef.current[fieldName] = focusReady;
            if (focusReady && !prevReady) {
              return Core__Option.forEach(CardFormShared$OrcaPaymentPage.nextFieldFor(fieldName), nextFieldName => {
                SadPortRegistry$OrcaPaymentPage.postFrame(portKey(groupId, nextFieldName), CardFormPortProtocol$OrcaPaymentPage.makePortFrame(CardFormPortProtocol$OrcaPaymentPage.kindDoFocus, true));
              });
            } else {
              return;
            }
          }
          console.warn(`[CardFormCoordinator] dropped port frame on unknown kind "` + kind + `" (port "` + key + `")`);
          return;
        }
        console.warn(`[CardFormCoordinator] dropped un-decodable port frame (port "` + key + `")`);
      };
      if (fieldName === "cardCvc" && lastBrandRef.current !== "") {
        return relayDetectedBrandToCvc(lastBrandRef.current);
      }
    });
  }, [
    registryVersion,
    groupId
  ]);
  React.useEffect(() => {
    let handleCommand = ev => {
      if (ev.source !== window.parent || keys.parentURL !== "*" && ev.origin !== keys.parentURL) {
        return;
      }
      let json = Utils$OrcaPaymentPage.safeParse(ev.data);
      let dict = Utils$OrcaPaymentPage.getDictFromJson(json);
      let command = Utils$OrcaPaymentPage.getString(dict, "cardFormCoordinatorCommand", "");
      if (command === "") {
        return;
      }
      let confirmId = Utils$OrcaPaymentPage.getString(dict, "confirmId", "");
      let errorLocale = Utils$OrcaPaymentPage.getString(dict, "locale", "en");
      if (coordinatorFamily === "OtherCoordinatorFamily") {
        return postConfirmResult(confirmId, buildConfirmResult({
          TAG: "Failure",
          _0: {
            code: "tokenization_failed",
            message: `cardFormCoordinator missing/unknown surfaceFamily (got "` + surfaceFamilyStr + `")`,
            locale: errorLocale,
            typeOverride: "ApiError"
          }
        }));
      }
      if (command !== "initiateConfirm") {
        return;
      }
      let flowKind = Utils$OrcaPaymentPage.getString(dict, "flow", "save");
      let isVaultCommand = flowKind === "save" || flowKind === "update";
      let misroute = isVaultCommand && coordinatorFamily !== "VaultCoordinator" || !isVaultCommand && coordinatorFamily !== "PaymentsCoordinator";
      if (misroute) {
        if (isVaultCommand) {
          return postConfirmResult(confirmId, buildConfirmResult({
            TAG: "Failure",
            _0: {
              code: "tokenization_failed",
              message: `vault "initiateConfirm" (` + flowKind + `) misrouted to a payments-family coordinator`,
              locale: errorLocale,
              typeOverride: "ApiError"
            }
          }));
        } else {
          return Utils$OrcaPaymentPage.messageParentWindow(keys.parentURL, [
            [
              "paymentConfirmFail",
              true
            ],
            [
              "errorMessage",
              `payments "initiateConfirm" (` + flowKind + `) misrouted to a vault-family coordinator`
            ],
            [
              "iframeId",
              keys.iframeId
            ]
          ]);
        }
      }
      if (flowKind === "payments" || flowKind === "savedCardCvc") {
        let cardNumber = aggregatedCardNumber();
        let cardExpiry = aggregatedCardExpiry();
        let cvcNumber = aggregatedCvcNumber();
        let cardBrand = aggregatedCardBrand();
        let cardFieldProblems = describeCardFieldProblems();
        let cvcProblem = cvcFieldProblem();
        if (flowKind === "payments" && cardFieldProblems !== "") {
          return Utils$OrcaPaymentPage.messageParentWindow(keys.parentURL, [
            [
              "paymentConfirmFail",
              true
            ],
            [
              "errorMessage",
              cardFieldProblems
            ],
            [
              "iframeId",
              keys.iframeId
            ]
          ]);
        }
        if (flowKind === "savedCardCvc" && Core__Option.isSome(cvcProblem)) {
          return Utils$OrcaPaymentPage.messageParentWindow(keys.parentURL, [
            [
              "paymentConfirmFail",
              true
            ],
            [
              "errorMessage",
              Core__Option.getOr(cvcProblem, "")
            ],
            [
              "iframeId",
              keys.iframeId
            ]
          ]);
        }
        let confirmParam_publishableKey = keys.publishableKey;
        let confirmParam = {
          return_url: Window$OrcaPaymentPage.hrefWithoutSearch,
          publishableKey: confirmParam_publishableKey
        };
        if (flowKind === "payments") {
          let match = CardUtils$OrcaPaymentPage.getExpiryDates(cardExpiry);
          let cardNetwork = [[
              "card_network",
              cardBrand !== "" ? cardBrand : null
            ]];
          let body = PaymentBody$OrcaPaymentPage.cardPaymentBody(cardNumber, match[0], match[1], Primitive_option.some(undefined), cvcNumber, cardNetwork, undefined);
          intent(false, body, confirmParam, keys.iframeId, undefined, undefined, undefined, undefined);
          return Utils$OrcaPaymentPage.messageParentWindow(keys.parentURL, [[
              "paymentConfirmAck",
              true
            ]]);
        }
        let paymentToken = Utils$OrcaPaymentPage.getString(dict, "paymentToken", "");
        let customerId;
        let exit = 0;
        if (typeof paymentMethodListValue !== "object") {
          customerId = "";
        } else {
          exit = 1;
        }
        if (exit === 1) {
          customerId = Utils$OrcaPaymentPage.getString(Utils$OrcaPaymentPage.getDictFromDict(Utils$OrcaPaymentPage.getDictFromJson(paymentMethodListValue._0), "intent_data"), "customer_id", "");
        }
        if (customerId === "") {
          return Utils$OrcaPaymentPage.messageParentWindow(keys.parentURL, [
            [
              "paymentConfirmFail",
              true
            ],
            [
              "errorMessage",
              "Customer payment method list is not loaded"
            ],
            [
              "iframeId",
              keys.iframeId
            ]
          ]);
        }
        let body$1 = PaymentBody$OrcaPaymentPage.savedCardBody(paymentToken, customerId, cvcNumber, true, true);
        intent(false, body$1, confirmParam, keys.iframeId, undefined, undefined, undefined, undefined);
        return Utils$OrcaPaymentPage.messageParentWindow(keys.parentURL, [[
            "paymentConfirmAck",
            true
          ]]);
      }
      if (confirmingRef.current) {
        return;
      }
      confirmingRef.current = true;
      let settledRef = {
        contents: false
      };
      let settle = result => {
        if (!settledRef.contents) {
          settledRef.contents = true;
          confirmingRef.current = false;
          return postConfirmResult(confirmId, result);
        }
      };
      let flow = Utils$OrcaPaymentPage.getString(dict, "flow", "save");
      let cardNumber$1 = aggregatedCardNumber();
      let cardExpiry$1 = aggregatedCardExpiry();
      let cvcNumber$1 = aggregatedCvcNumber();
      let match$1;
      if (typeof vaultCredentials !== "object" || vaultCredentials.TAG !== "HyperswitchVault") {
        match$1 = [
          "",
          ""
        ];
      } else {
        let creds = vaultCredentials._0;
        match$1 = [
          creds.pmSessionId,
          creds.sdkAuthorization
        ];
      }
      let vaultAuth = match$1[1];
      let pmSessionId = match$1[0];
      let cardFieldProblems$1 = describeCardFieldProblems();
      let cvcProblem$1 = cvcFieldProblem();
      if (flow !== "update" && cardFieldProblems$1 !== "") {
        return settle(buildConfirmResult({
          TAG: "Failure",
          _0: {
            code: "validation_error",
            message: cardFieldProblems$1,
            locale: errorLocale,
            typeOverride: undefined
          }
        }));
      }
      if (flow === "update" && Core__Option.isSome(cvcProblem$1)) {
        return settle(buildConfirmResult({
          TAG: "Failure",
          _0: {
            code: "validation_error",
            message: cvcProblem$1,
            locale: errorLocale,
            typeOverride: undefined
          }
        }));
      }
      if (pmSessionId === "" || vaultAuth === "") {
        return settle(buildConfirmResult({
          TAG: "Failure",
          _0: {
            code: "tokenization_failed",
            message: "no hyperswitch vault credentials decoded from sessions",
            locale: errorLocale,
            typeOverride: "ApiError"
          }
        }));
      }
      if (flow === "save") {
        let match$2 = CardUtils$OrcaPaymentPage.getExpiryDates(cardExpiry$1);
        Core__Promise.$$catch(PaymentHelpersV2$OrcaPaymentPage.savePaymentMethod(PaymentBody$OrcaPaymentPage.cardTokenizationBody(cardNumber$1, match$2[0], match$2[1], cvcNumber$1), pmSessionId, vaultAuth, loggerState).then(response => {
          if (response === null) {
            settle(buildConfirmResult({
              TAG: "Failure",
              _0: {
                code: "tokenization_failed",
                message: "vault confirm POST returned null (HTTP failure)",
                locale: errorLocale,
                typeOverride: "ApiError"
              }
            }));
          } else {
            settle(buildConfirmResult({
              TAG: "Success",
              _0: response
            }));
          }
          return Promise.resolve();
        }), param => {
          settle(buildConfirmResult({
            TAG: "Failure",
            _0: {
              code: "tokenization_failed",
              message: "vault confirm POST rejected",
              locale: errorLocale,
              typeOverride: "ApiError"
            }
          }));
          return Promise.resolve();
        });
        return;
      }
      Core__Promise.$$catch(PaymentHelpersV2$OrcaPaymentPage.updatePaymentMethod(PaymentManagementBody$OrcaPaymentPage.vaultUpdateCVVBody(cvcNumber$1), pmSessionId, loggerState, customPodUri, vaultAuth).then(response => {
        if (response === null) {
          settle(buildConfirmResult({
            TAG: "Failure",
            _0: {
              code: "tokenization_failed",
              message: "vault update POST returned null (HTTP failure)",
              locale: errorLocale,
              typeOverride: "ApiError"
            }
          }));
        } else {
          settle(buildConfirmResult({
            TAG: "Success",
            _0: response
          }));
        }
        return Promise.resolve();
      }), param => {
        settle(buildConfirmResult({
          TAG: "Failure",
          _0: {
            code: "tokenization_failed",
            message: "vault update POST rejected",
            locale: errorLocale,
            typeOverride: "ApiError"
          }
        }));
        return Promise.resolve();
      });
    };
    return Utils$OrcaPaymentPage.handleMessage(handleCommand, "");
  }, [
    keys,
    sessions,
    paymentMethodListValue,
    registryVersion,
    groupId
  ]);
  return null;
}

let make = CardFormCoordinator;

export {
  errorTypeToString,
  defaultErrorMessage,
  resolveErrorMessage,
  makeErrorResult,
  buildConfirmResult,
  isErrorResult,
  portKey,
  make,
}
/* jotai Not a pure module */
